
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';
import { FormField } from '@/components/forms/FormField';
import { PasswordField } from '@/components/forms/PasswordField';
import { signupSchema, type SignupFormData } from '@/lib/validations/auth';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  trackSignupStarted,
  trackSignupCompleted,
  linkAttributionToUser,
} from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { CaptchaField, type CaptchaFieldHandle } from '@/components/Auth/CaptchaField';
import { verifyCaptcha } from '@/lib/captcha';

const Signup: React.FC = () => {
  const { toast } = useToast();
  const { signUp, signInWithGoogle, user } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setError,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      acceptTerms: false,
    },
  });

  const watchedPassword = watch('password');
  const [success, setSuccess] = React.useState(false);
  const startedTrackedRef = React.useRef(false);
  const [captchaToken, setCaptchaToken] = React.useState('');
  const captchaRef = React.useRef<CaptchaFieldHandle | null>(null);

  // COP-11 — fire signup_started once when the form mounts (skipped when the user is already
  // authed and we're about to redirect).
  React.useEffect(() => {
    if (user) return;
    if (startedTrackedRef.current) return;
    startedTrackedRef.current = true;
    void trackSignupStarted({ segment: 'planner' });
  }, [user]);

  // Redirect if already authenticated. Hooks must run unconditionally above this guard.
  if (user) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const onSubmit = async (data: SignupFormData) => {
    try {
      // Captcha must be solved AND server-verified before we hit Supabase Auth.
      if (!captchaToken) {
        setError('root', { message: 'Por favor verifica que no eres un robot.' });
        return;
      }

      const captchaResult = await verifyCaptcha(captchaToken, 'signup');
      if (!captchaResult.success) {
        captchaRef.current?.reset();
        setCaptchaToken('');
        toast({
          variant: 'destructive',
          title: 'Captcha inválido',
          description: 'No pudimos verificar el captcha. Inténtalo de nuevo.',
        });
        return;
      }

      const { error } = await signUp(data.email, data.password, data.name);

      if (error) {
        if (error.message.includes('User already registered')) {
          setError('email', {
            message: 'Este correo ya está registrado. Intenta iniciar sesión.',
          });
        } else {
          setError('root', {
            message: error.message,
          });
        }

        toast({
          variant: "destructive",
          title: "Error al crear cuenta",
          description: error.message,
        });
        return;
      }

      setSuccess(true);

      // COP-11 — link the anon attribution row to this auth user (best-effort, fire-and-forget)
      // and emit signup_completed. Supabase's signUp does not always return the new user inline
      // when email confirmation is enabled, so we read it from getUser().
      try {
        const { data: userData } = await supabase.auth.getUser();
        const newUserId = userData?.user?.id;
        if (newUserId) await linkAttributionToUser(newUserId);
      } catch { /* analytics must never block signup UX */ }
      void trackSignupCompleted({ segment: 'planner' });

      toast({
        title: "¡Cuenta creada exitosamente!",
        description: `Te hemos enviado un correo de confirmación a ${data.email}`,
      });
    } catch (error) {
      setError('root', {
        message: 'Error inesperado al crear la cuenta. Por favor intenta de nuevo.',
      });
      
      toast({
        variant: "destructive",
        title: "Error al crear cuenta",
        description: "Hubo un problema al crear tu cuenta. Por favor intenta de nuevo.",
      });
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const { error } = await signInWithGoogle();
      
      if (error) {
        toast({
          variant: "destructive",
          title: "Error con Google",
          description: error.message,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error con Google",
        description: "No se pudo registrar con Google. Por favor intenta de nuevo.",
      });
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PublicNavbar />
        <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-brand sm:rounded-lg sm:px-10 text-center border border-neutral-200">
              <div className="w-16 h-16 bg-semantic-success-light rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-semantic-success" />
              </div>
              <h2 className="text-h2 font-semibold mb-4 text-brand-primary">
                ¡Cuenta creada exitosamente!
              </h2>
              <p className="text-body-md text-neutral-600 mb-6">
                Te hemos enviado un correo de confirmación a tu email. 
                Revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.
              </p>
              <Link to="/login">
                <Button className="btn-primary shadow-sm">
                  Ir a iniciar sesión
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicNavbar />
      <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-h1 font-bold text-brand-primary">
              Crea tu cuenta gratuita
            </h2>
            <p className="mt-2 text-body-md text-neutral-600">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="font-medium text-brand-primary hover:text-brand-primary-hover transition-colors focus-visible-ring">
                Inicia sesión
              </Link>
            </p>
          </div>

          <div className="bg-white py-8 px-4 shadow-brand sm:rounded-lg sm:px-10 border border-neutral-200">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {errors.root && (
                <div role="alert" className="bg-semantic-error-light border border-semantic-error text-semantic-error px-4 py-3 rounded-md">
                  {errors.root.message}
                </div>
              )}

              <FormField
                label="Nombre completo"
                name="name"
                type="text"
                autoComplete="name"
                required
                autoFocus
                placeholder="Tu nombre completo"
                error={errors.name}
                {...register('name')}
              />

              <FormField
                label="Correo electrónico"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="tu@email.com"
                error={errors.email}
                {...register('email')}
              />

              <PasswordField
                label="Contraseña"
                name="password"
                autoComplete="new-password"
                required
                placeholder="Mínimo 8 caracteres"
                error={errors.password}
                value={watchedPassword}
                showStrength={true}
                {...register('password')}
              />

              <div className="flex items-start">
                <input
                  id="accept-terms"
                  type="checkbox"
                  className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-neutral-300 rounded mt-1"
                  {...register('acceptTerms')}
                />
                <Label htmlFor="accept-terms" className="ml-2 block text-body-sm text-neutral-700">
                  Acepto los{' '}
                  <Link to="/terminos" className="text-brand-primary hover:text-brand-primary-hover underline focus-visible-ring">
                    términos y condiciones
                  </Link>{' '}
                  y la{' '}
                  <Link to="/aviso-de-privacidad" className="text-brand-primary hover:text-brand-primary-hover underline focus-visible-ring">
                    política de privacidad
                  </Link>
                </Label>
              </div>
              
              {errors.acceptTerms && (
                <p className="text-body-sm text-semantic-error mt-1" role="alert">
                  {errors.acceptTerms.message}
                </p>
              )}

              <CaptchaField
                ref={captchaRef}
                onVerify={(token) => setCaptchaToken(token)}
              />

              <div>
                <Button
                  type="submit"
                  disabled={isSubmitting || !captchaToken}
                  className="w-full btn-primary shadow-sm"
                >
                  {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta y obtener evento gratis'}
                </Button>
              </div>

              <div className="text-center">
                <p className="text-body-sm text-neutral-600">
                  Recibirás un correo de confirmación para activar tu cuenta
                </p>
              </div>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-300" />
                  </div>
                  <div className="relative flex justify-center text-body-sm">
                    <span className="px-2 bg-white text-neutral-500">o</span>
                  </div>
                </div>

                <div className="mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleSignup}
                    className="w-full btn-outline"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continuar con Google
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
